import React from "react";
import { Box, Text, Divider, HStack, Badge } from "@chakra-ui/react";
import colors from "../../helpers/colors";

// Pull the terminal `survey_result` message out of the conversation, if
// any. Survey-mode conversations never write a chat-style evaluation
// (saved_evaluation_summary is a chat-only tool call), so without this
// path the report card was permanently empty.
function findSurveyResult(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.metadata?.kind !== "survey_result") continue;
    try {
      return JSON.parse(m.content);
    } catch {
      return null;
    }
  }
  return null;
}

const MyaReportSection = ({ evaluation, messages }) => {
  // CandidateDetails also threads `candidate` / `parsedMetadata` props here;
  // they're unused so we don't destructure them — React ignores extra props.
  const surveyResult = findSurveyResult(messages);
  const hasChatEvaluation = !!(evaluation && evaluation.aiSummary);

  if (!hasChatEvaluation && !surveyResult) {
    return (
      <Box
        backgroundColor={colors.black}
        borderRadius="12px"
        p="24px"
        mb="16px"
      >
        <Text fontSize="xl" fontWeight="bold" color="white" mb="16px">
          MYA Report
        </Text>
        <Text fontSize="sm" color="gray.400">
          No MYA report available for this candidate.
        </Text>
      </Box>
    );
  }

  // Survey-mode report. The verdict + summary lives on the survey_result
  // chunk we just dug out; the decisionSource tells the recruiter whether
  // the deterministic check or the LLM tiebreaker decided it.
  if (surveyResult) {
    const passed = surveyResult.verdict === "Pass";
    return (
      <Box p="12px" mb="0px">
        <HStack mb="12px" spacing="12px">
          <Text fontSize="xl" fontWeight="bold" color="white">
            MYA Report
          </Text>
          <Badge colorScheme={passed ? "green" : "orange"} fontSize="xs">
            {surveyResult.verdict}
          </Badge>
          {surveyResult.decisionSource && (
            <Text fontSize="xs" color="gray.400">
              {surveyResult.decisionSource === "Deterministic"
                ? "Deterministic check"
                : "LLM tiebreaker"}
            </Text>
          )}
        </HStack>
        {surveyResult.summary && (
          <Text
            fontSize="sm"
            color="white"
            lineHeight="1.6"
            whiteSpace="pre-line"
          >
            {surveyResult.summary}
          </Text>
        )}
        <Divider mt="32px" borderColor="gray.600" />
      </Box>
    );
  }

  // Legacy chat-mode evaluation path — unchanged behaviour for any
  // conversations that still go through Mya the chatbot.
  return (
    <Box p="12px" mb="0px">
      <Text fontSize="xl" fontWeight="bold" color="white" mb="16px">
        MYA Report
      </Text>
      <Text fontSize="sm" color="white" lineHeight="1.6" whiteSpace="pre-line">
        {evaluation.aiSummary}
      </Text>
      <Divider mt="32px" borderColor="gray.600" />
    </Box>
  );
};

export default MyaReportSection;
